/**
 * useCompanyScoping — per-module company-scoping participation (multi-company WI-4).
 *
 * Reads the server-cached `/api/v1/tenant/company-scoping` map (`{ moduleId:
 * enabled }`), available to any authenticated user, and feeds it into the
 * non-React `companyScopingStore` so the axios interceptor and pure helpers see
 * the same value. `useCompanyScopingSync()` is mounted once near the app root
 * (in `ActiveCompanyProvider`); components that need the value reactively use
 * `useCompanyScoping()`.
 *
 * Gates BOTH multi-company affordances: the app-bar `CompanyScopeSwitcher` and
 * the on-record `DocumentCompanyField`. The tenant-admin toggle flips a module's
 * flag via `useSetCompanyScoping()` and invalidates this query.
 */
import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { tenantSettingsApi, type CompanyScopingMap } from '../services/tenantSettingsApi';
import { useAuth } from '../auth';
import {
  isModuleCompanyScoped,
  setCompanyScopingMap,
  useCompanyScopingMapStore,
} from '../utils/companyScopingStore';

export const COMPANY_SCOPING_QUERY_KEY = ['company-scoping'];

function useCompanyScopingQuery() {
  const { user } = useAuth();
  return useQuery<CompanyScopingMap>({
    queryKey: COMPANY_SCOPING_QUERY_KEY,
    queryFn: () => tenantSettingsApi.getCompanyScoping(),
    enabled: !!user?.tenant_id,
    // Participation changes rarely; match the server-side L2 cache window.
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch the participation map and sync it into the external store. Mount ONCE
 * near the app root. When the map changes, invalidate cached queries so list
 * surfaces refetch with the (now possibly added) `X-Active-Company-Id` header.
 */
export function useCompanyScopingSync(): void {
  const queryClient = useQueryClient();
  const { data } = useCompanyScopingQuery();
  const lastKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setCompanyScopingMap(data);
    const key = JSON.stringify(data);
    if (lastKeyRef.current !== null && lastKeyRef.current !== key) {
      // A toggle changed which modules send the company filter — refetch lists.
      queryClient.invalidateQueries();
    }
    lastKeyRef.current = key;
  }, [data, queryClient]);
}

/**
 * Reactive access to the participation map. Re-renders when the map loads or a
 * toggle changes. `isModuleScoped(moduleId)` falls back to the always-on ERP
 * set before the map loads (see `companyScopingStore`).
 */
export function useCompanyScoping() {
  const map = useCompanyScopingMapStore();
  return {
    map,
    isLoading: map === null,
    isModuleScoped: (moduleId: string) => isModuleCompanyScoped(moduleId),
  };
}

/** Toggle an opt-in module's company-scoping participation (tenant admin). */
export function useSetCompanyScoping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ moduleId, enabled }: { moduleId: string; enabled: boolean }) =>
      tenantSettingsApi.setCompanyScoping(moduleId, enabled),
    onSuccess: (map) => {
      // Push the authoritative map straight into the store (snappy switcher/field
      // update) and into the query cache. Writing the query data re-runs
      // useCompanyScopingSync, which invalidates list surfaces on the change — so
      // no extra invalidateQueries for this key is needed here.
      setCompanyScopingMap(map);
      queryClient.setQueryData(COMPANY_SCOPING_QUERY_KEY, map);
    },
  });
}
